import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  i18n,
  type SupportedLanguage,
  type Namespace,
  SUPPORTED_LANGUAGES,
} from '@/lib/i18n';
import { useAuthStore } from '@/stores/auth.store';

// ============================================================================
// ============================================================================

/**
 */
export const SECONDARY_LANGUAGE_STORAGE_KEY = 'i18nextSecondaryLng';

// ============================================================================
// ============================================================================

export interface LanguageSettings {
  primaryLanguage: SupportedLanguage;
  secondaryLanguage: SupportedLanguage | null;
  isSecondaryLoaded: boolean;
  setPrimaryLanguage: (lng: SupportedLanguage) => void;
  setSecondaryLanguage: (lng: SupportedLanguage | null) => void;
}

// ============================================================================
// Context
// ============================================================================

const LanguageSettingsContext = createContext<LanguageSettings | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface LanguageSettingsProviderProps {
  children: ReactNode;
}

export function LanguageSettingsProvider({ children }: LanguageSettingsProviderProps) {
  const { i18n: i18nInstance } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const hasSyncedFromServer = useRef(false);

  const [secondaryLanguage, setSecondaryLng] = useState<SupportedLanguage | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(SECONDARY_LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }
    return null;
  });

  const [isSecondaryLoaded, setIsSecondaryLoaded] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.secondaryLanguage && !hasSyncedFromServer.current) {
      const serverLang = user.secondaryLanguage as SupportedLanguage;
      if (SUPPORTED_LANGUAGES.includes(serverLang)) {
        setSecondaryLng(serverLang);
        localStorage.setItem(SECONDARY_LANGUAGE_STORAGE_KEY, serverLang);
        hasSyncedFromServer.current = true;
        if (import.meta.env.DEV) {
          console.log('[LanguageSettings] Synced secondary language from server:', serverLang);
        }
      }
    }
    if (!isAuthenticated) {
      hasSyncedFromServer.current = false;
    }
  }, [isAuthenticated, user?.secondaryLanguage]);

  /**
   */
  const loadSecondaryLanguageResources = useCallback(async (lng: SupportedLanguage) => {
    setIsSecondaryLoaded(false);

    if (i18n.hasResourceBundle(lng, 'common')) {
      setIsSecondaryLoaded(true);
      return;
    }

    try {
      await i18n.loadLanguages(lng);
      setIsSecondaryLoaded(true);

      if (import.meta.env.DEV) {
        console.log('[LanguageSettings] Secondary language loaded:', lng);
      }
    } catch (error) {
      console.error('[LanguageSettings] Failed to load secondary language:', lng, error);
      setIsSecondaryLoaded(false);
    }
  }, []);

  /**
   */
  const setPrimaryLanguage = useCallback((lng: SupportedLanguage) => {
    i18nInstance.changeLanguage(lng);
  }, [i18nInstance]);

  /**
   */
  const setSecondaryLanguage = useCallback((lng: SupportedLanguage | null) => {
    setSecondaryLng(lng);

    if (lng) {
      localStorage.setItem(SECONDARY_LANGUAGE_STORAGE_KEY, lng);
    } else {
      localStorage.removeItem(SECONDARY_LANGUAGE_STORAGE_KEY);
      setIsSecondaryLoaded(false);
    }
  }, []);

  useEffect(() => {
    if (secondaryLanguage && secondaryLanguage !== i18nInstance.language) {
      loadSecondaryLanguageResources(secondaryLanguage);
    } else {
      setIsSecondaryLoaded(secondaryLanguage === null ? false : true);
    }
  }, [secondaryLanguage, i18nInstance.language, loadSecondaryLanguageResources]);

  useEffect(() => {
    if (secondaryLanguage === i18nInstance.language) {
      if (import.meta.env.DEV) {
        console.log('[LanguageSettings] Secondary language cleared (same as primary)');
      }
      setSecondaryLanguage(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18nInstance.language]);

  const value = useMemo<LanguageSettings>(() => ({
    primaryLanguage: i18nInstance.language as SupportedLanguage,
    secondaryLanguage,
    isSecondaryLoaded,
    setPrimaryLanguage,
    setSecondaryLanguage,
  }), [
    i18nInstance.language,
    secondaryLanguage,
    isSecondaryLoaded,
    setPrimaryLanguage,
    setSecondaryLanguage,
  ]);

  return (
    <LanguageSettingsContext.Provider value={value}>
      {children}
    </LanguageSettingsContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 */
export function useLanguageSettings(): LanguageSettings {
  const context = useContext(LanguageSettingsContext);
  if (!context) {
    throw new Error('useLanguageSettings must be used within a LanguageSettingsProvider');
  }
  return context;
}

/**
 *
 *
 * @example
 * ```tsx
 * const { tSecondary } = useSecondaryTranslation('common');
 *
 * <Tooltip content={tSecondary ? tSecondary('button.save') : t('button.save')}>
 *   <Button>{t('button.save')}</Button>
 * </Tooltip>
 * ```
 */
export function useSecondaryTranslation(ns: Namespace | Namespace[] = 'common'): {
  tSecondary: TFunction | null;
  isLoaded: boolean;
  hasSecondaryLanguage: boolean;
} {
  const { secondaryLanguage, isSecondaryLoaded } = useLanguageSettings();

  const tSecondary = useMemo(() => {
    if (!secondaryLanguage || !isSecondaryLoaded) {
      return null;
    }

    const namespace = Array.isArray(ns) ? ns[0] : ns;
    return i18n.getFixedT(secondaryLanguage, namespace);
  }, [secondaryLanguage, isSecondaryLoaded, ns]);

  return {
    tSecondary,
    isLoaded: isSecondaryLoaded,
    hasSecondaryLanguage: secondaryLanguage !== null,
  };
}
